import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { FileText, Shield } from 'lucide-react';
import { PrivacyPolicy } from './auth/PrivacyPolicy';
import { TermsOfUse } from './auth/TermsOfUse';
import logoIcon from '@/assets/loop-icon.png';

interface AboutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AboutModal({ open, onOpenChange }: AboutModalProps) {
  const version = '1.5.3';
  const releaseDate = 'November 2025';
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <img src={logoIcon} alt="Patent Loop" className="h-10 w-auto" />
              <DialogTitle className="text-2xl font-bold">Patent Loop</DialogTitle>
              <span className="px-2.5 py-1 text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md">
                BETA
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Version {version} • {releaseDate}
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* About Section */}
            <div>
              <h3 className="text-lg font-semibold mb-2">About Patent Loop</h3>
              <p className="text-sm text-gray-700">
                Patent Loop is a comprehensive patent analysis and prosecution management platform designed to streamline
                patent examination workflows, office action analysis, and patent research.
              </p>
            </div>

            <Separator />

            {/* Legal Documents */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Legal Information</h3>
              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  className="justify-start gap-2 h-auto py-3"
                  onClick={() => setShowPrivacyPolicy(true)}
                >
                  <Shield className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <div className="font-semibold">Privacy Policy</div>
                    <div className="text-xs text-gray-600">Learn how we protect your data</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="justify-start gap-2 h-auto py-3"
                  onClick={() => setShowTermsOfUse(true)}
                >
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <div className="font-semibold">Terms of Use</div>
                    <div className="text-xs text-gray-600">Review our terms and conditions</div>
                  </div>
                </Button>
              </div>
            </div>

            <Separator />

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Contact Us</h3>
              <p className="text-sm text-gray-700">
                For questions or support, please contact us at{' '}
                <a href="mailto:support@forgen.ai" className="text-blue-600 hover:underline">
                  support@forgen.ai
                </a>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Modal */}
      <PrivacyPolicy
        open={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />

      {/* Terms of Use Modal */}
      <TermsOfUse
        open={showTermsOfUse}
        onClose={() => setShowTermsOfUse(false)}
      />
    </>
  );
}
